<template>
    <form>
        <ErrorSummary :response-status="responseStatus" />
        <div class="grid grid-cols-4 gap-6 mt-6">

            <div class="col-span-4 sm:col-span-4">
                <label class="block text-sm font-medium text-gray-700" for="profileDisplayName">Profile Display Name
                    <SparklesIcon class="inline w-4 h-4 mx-1 text-powder-600"></SparklesIcon>
                </label>
                <input id="profileDisplayName" v-model="profile.profileDisplayName"
                    class="block w-full px-3 py-2 mt-1 border rounded-md shadow-sm border-clay-500 focus:outline-hidden focus:ring-clay-800 focus:border-clay-500 sm:text-sm"
                    name="profileDisplayName" type="text">
            </div>

            <div class="col-span-4 sm:col-span-4">
                <label class="block text-sm font-medium text-gray-700">Region</label>
                <TerritoriesTab :territories="territories" v-model="userPreferredRegion"
                    @onRegionSelected="onRegionSelected" />
            </div>

        </div>
        <div class="">
            <div class="divide-y divide-gray-200">
                <div class="grid grid-cols-4 gap-6 mt-6">
                    <div class="col-span-4">
                        <div class="z-40" x-cloak x-data="dropdown">
                            <div class="items-center justify-between text-sm">
                                <div class="">
                                    <span class="inline-block text-sm font-medium text-powder-800">Introduction</span>
                                    <button aria-controls="introSample" aria-expanded="true" aria-label="Toggle Introduction Sample"
                                        class="inline-block pl-3 font-medium text-powder-600 hover:text-powder-500"
                                        type="button" x-on:click="toggle">
                                        <i class="far fa-question-circle"></i>
                                    </button>
                                </div>
                            </div>

                            <div id="introSample" aria-labelledby="menu-button" aria-orientation="vertical"
                                class="items-center justify-between p-5 my-2 mb-5 bg-powder-50 rounded-2xl" role="menu"
                                tabindex="-1" x-show="open">
                                <span class="block font-medium">Sample Introduction:</span>

                                Hello and welcome to my StageAgent page! I’ve performed leading roles in both amateur
                                and professional theatre productions, playing characters from evil villains to extreme
                                comic buffoons. I am a classically trained vocalist, an expert tap dancer and studied
                                acting under Professor Edward Graves at NYU. I currently reside in New York City and am
                                actively looking for new engagements.
                            </div>

                        </div>
                        <label for="intro" class="block text-sm font-medium text-gray-700 sr-only">Introduction</label>
                        <div class="mt-1">
                          <textarea v-model="profile.intro"
                                class="block w-full px-3 py-2 mt-1 border rounded-md shadow-sm border-clay-500 focus:outline-hidden focus:ring-clay-800 focus:border-clay-500 sm:text-sm"
                                id="intro" name="intro" rows="4"></textarea>
                        </div>
                        <p class="max-w-2xl mt-1 text-sm text-clay-800">Maximum characters: 400</p>
                    </div>

                </div>
            </div>
        </div>
        <div class="mt-10 divide-y divide-gray-200">
            <div class="space-y-1">
                <h3 class="text-xl font-bold leading-6 text-gray-900">Roles
                    <SparklesIcon class="inline w-4 h-4 mx-1 text-powder-600"></SparklesIcon>
                </h3>
                <p class="max-w-2xl text-sm text-clay-800">Select your areas of expertise</p>
            </div>
            <div class="mt-6">
                <div class="px-6 pb-6 divide-y bg-clay-50 divide-clay-200">
                    <h2 class="max-w-2xl pt-6 font-bold text-clay-900">Performer</h2>
                    <div class="mt-6">
                        <div class="">
                            <div>
                                <fieldset class="space-y-5">
                                    <legend class="sr-only">Performer types</legend>
                                    <div class="grid gap-1 sm:grid-cols-2 md:grid-cols-3">
                                        <div v-for="option in profile.performerRolesOptions"
                                            class="relative flex items-start">
                                            <div class="flex items-center h-5">
                                                <input :id="option" v-model="profile.performerRoles" :name="option"
                                                    :value="option" aria-describedby=""
                                                    class="w-4 h-4 rounded border-clay-500 text-powder-600 focus:ring-powder-500"
                                                    type="checkbox">
                                            </div>
                                            <div class="ml-3 text-sm">
                                                <label :for="option" class="font-medium text-gray-700">{{
                                                    option
                                                    }}</label> <span class="text-clay-800"></span>
                                            </div>
                                        </div>
                                    </div>
                                </fieldset>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="px-6 pb-6 divide-y bg-clay-50 divide-clay-200">
                    <h2 class="max-w-2xl pt-6 font-bold text-clay-900">Artistic Staff</h2>
                    <div class="mt-6">
                        <div class="">
                            <div>
                                <fieldset class="space-y-5">
                                    <legend class="sr-only">Artistic staff types</legend>
                                    <div class="grid gap-1 sm:grid-cols-2 md:grid-cols-3">
                                        <div v-for="option in profile.staffRolesOptions"
                                            class="relative flex items-start">
                                            <div class="flex items-center h-5">
                                                <input :id="option" v-model="profile.staffRoles" :name="option"
                                                    :value="option" aria-describedby=""
                                                    class="w-4 h-4 rounded border-clay-500 text-powder-600 focus:ring-powder-500"
                                                    type="checkbox">
                                            </div>
                                            <div class="ml-3 text-sm">
                                                <label :for="option" class="font-medium text-gray-700">{{
                                                    option
                                                    }}</label> <span class="text-clay-800"></span>
                                            </div>
                                        </div>
                                    </div>
                                </fieldset>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="px-6 pb-6 divide-y bg-clay-50 divide-clay-200">
                    <h2 class="max-w-2xl pt-6 font-bold text-clay-900">Teacher</h2>
                    <div class="mt-6">
                        <div>
                            <div>
                                <fieldset class="space-y-5">
                                    <legend class="sr-only">Teacher types</legend>
                                    <div class="grid gap-1 sm:grid-cols-2 md:grid-cols-3">
                                        <div v-for="option in profile.teacherRolesOptions"
                                            class="relative flex items-start">
                                            <div class="flex items-center h-5">
                                                <input :id="option" v-model="profile.teacherRoles" :name="option"
                                                    :value="option" aria-describedby=""
                                                    class="w-4 h-4 rounded border-clay-500 text-powder-600 focus:ring-powder-500"
                                                    type="checkbox">
                                            </div>
                                            <div class="ml-3 text-sm">
                                                <label :for="option" class="font-medium text-gray-700">{{
                                                    option
                                                    }}</label> <span class="text-clay-800"></span>
                                            </div>
                                        </div>
                                    </div>
                                </fieldset>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>



        <div class="py-3 mt-10 text-center">
            <SaButton @click.prevent="submit" :loading="isSubmitting" variant="primary"/>
        </div>


    </form>

    <NotificationSuccess :show="updateSuccessful" content="Your changes were successfully saved."
        title="Successfully saved!" />
</template>

<script lang="ts" setup>
// @ts-ignore
import {computed, defineEmits, onMounted, Ref, ref} from "vue";
import {client} from '@/shared';
import {
    GetProfileRegion, GetProfileRegionResponse,
    GetProfileSettingBasics,
    GetProfileSettingBasicsResponse,
    Region, UpdateProfileRegion,
    UpdateProfileSettingBasics
} from "@/dtos";
import {
  GetAllTerritories
} from "@/dtos";
// @ts-ignore
import {SparklesIcon, TrashIcon} from '@heroicons/vue/20/solid';
import {ResponseStatus} from "@servicestack/client";
import ErrorSummary from "@/main/components/ErrorSummary.vue";
import NotificationSuccess from "@/main/components/NotificationSuccess.vue";
import TerritoriesTab from "@/../Features/Territories/TerritoriesTab.vue";
import SaButton from "@/main/components/SaButton.vue";

let responseStatus = ref(null);
let profile: Ref<GetProfileSettingBasicsResponse> = ref(new GetProfileSettingBasicsResponse());
let updateSuccessful: Ref<Boolean> = ref(false);

const territories: Ref<Array<Region>> = ref([]);
const userPreferredRegion: Ref<Region> = ref(new Region());
const territoryQuery = ref('');

onMounted(async () => {
    await client.get<Array<Region>>(new GetAllTerritories())
        .then(res => {
            territories.value = res.sort((a: Region, b: Region) => a.name.localeCompare(b.name));
        });

    await client.get<GetProfileSettingBasicsResponse>(new GetProfileSettingBasics())
        .then(response => {
            profile.value = response;
        });

    await client.get<GetProfileRegionResponse>(new GetProfileRegion())
        .then(res => {
            userPreferredRegion.value = res.closestRegionForUser;
        });
});

let filteredTerritories = computed(() =>
    territoryQuery.value === ''
        ? territories.value
        : territories.value.filter((territory: Region) => {
              return territory.name.toLowerCase().includes(territoryQuery.value.toLowerCase())
            }
        )
)

const onRegionSelected = async (territory: Region) => {
    userPreferredRegion.value = territory;
    updateSuccessful.value = false;
    await client.post(new UpdateProfileRegion({territoryId: userPreferredRegion.value.id}))
        .then(response => {
            updateSuccessful.value = true;
            responseStatus.value = null;
        })
        .catch(e => {
            responseStatus.value = e.responseStatus;
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        });
}

const isSubmitting = ref(false);
const submit = async () => {
  isSubmitting.value = true;
  updateSuccessful.value = false;
  let request = profile.value;
  
  await client.post<ResponseStatus>(new UpdateProfileSettingBasics(request))
      .then(_ => {
        updateSuccessful.value = true;
        responseStatus.value = null;
      })
      .catch(e => {
        responseStatus.value = e.responseStatus;
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      });
    isSubmitting.value = false;
}
</script>
<style>

</style>